/* eslint-disable react/prop-types */
import { FC } from "react";
import { useUser } from "src/hooks";
import dayjs from "dayjs";
import { REACTIONS } from "src/features/SocialFeedContent/components/ReactionPicker/ReactionPicker";
import ImpersonationBanner from "src/components/ImpersonationBanner/ImpersonationBanner";

interface CommentItemProps {
	userId: string;
	userName?: string | null;
	body: string;
	date: string;
	reactions: CommentReaction[];
	isReply?: boolean;
	impersonatedBy?: {
		firstName: string;
		lastName: string;
		avatarUrl: string | null;
	} | null;
}

/**
 * Read-only comment row. Shows author, body, timestamp, and a passive
 * reactions summary. No reply / delete / react buttons — admins mirror
 * comments from the app rather than participating.
 */
const CommentItem: FC<CommentItemProps> = ({
	userId,
	userName,
	body,
	date,
	reactions,
	isReply = false,
	impersonatedBy
}) => {
	const needsFetch = !userName && Boolean(userId);
	const { data: user } = useUser(userId, false, {
		enabled: needsFetch
	});

	const displayName = userName || user?.name || null;

	const initials = displayName
		? displayName
				.split(" ")
				.slice(0, 2)
				.map((n: string) => n[0])
				.join("")
				.toUpperCase()
		: "";

	const totalReactions = reactions.reduce((sum, r) => sum + r.count, 0);
	const distinctReactions = reactions
		.filter((r) => r.count > 0)
		.map((r) => REACTIONS.find((def) => def.value === r.reaction))
		.filter((r): r is (typeof REACTIONS)[number] => Boolean(r));

	return (
		<div className={isReply ? "ml-8 border-l-2 border-zinc-200 pl-3" : ""}>
			<div className="flex gap-2.5 py-2">
				<div className="w-6 h-6 shrink-0 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-[10px] font-medium">
					{initials}
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="text-[13px] font-medium text-foreground">
							{displayName ?? "Unknown user"}
						</span>
						<span className="text-[11px] text-muted-foreground">
							{dayjs(date).format("DD MMM YYYY, HH:mm")}
						</span>
					</div>
					{impersonatedBy && (
						<ImpersonationBanner
							variant="compact"
							impersonatedBy={{
								firstName: impersonatedBy.firstName,
								lastName: impersonatedBy.lastName,
								avatarUrl: impersonatedBy.avatarUrl
							}}
						/>
					)}
					<p className="text-[13px] text-foreground mt-0.5">{body}</p>
					{totalReactions > 0 && (
						<div className="flex items-center gap-3 mt-1">
							<span
								aria-label={`${totalReactions} ${
									totalReactions === 1 ? "reaction" : "reactions"
								}: ${distinctReactions.map((r) => r.name).join(", ")}`}
								className="flex items-center gap-1 text-[11px] text-muted-foreground"
							>
								{distinctReactions.length > 0 && (
									<span
										aria-hidden="true"
										className="flex items-center -space-x-1"
									>
										{distinctReactions.map((r) => (
											<span
												key={r.value}
												className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] shadow-xs ring-1 ring-border"
											>
												{r.emoji}
											</span>
										))}
									</span>
								)}
								<span aria-hidden="true">{totalReactions}</span>
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

interface CommentThreadProps {
	comments: SocialComment[];
	hasMore: boolean;
	isLoadingMore: boolean;
	onLoadMore: () => void;
}

const CommentThread: FC<CommentThreadProps> = ({
	comments,
	hasMore,
	isLoadingMore,
	onLoadMore
}) => {
	return (
		<div className="border-t border-border">
			<div className="px-4 pb-2">
				{comments.length === 0 && (
					<div className="px-4 py-6 text-center">
						<p className="text-sm text-muted-foreground">No comments yet</p>
					</div>
				)}
				<div className="divide-y divide-border/50">
					{comments.map((comment) => (
						<div key={comment.id}>
							<CommentItem
								userId={comment.user}
								userName={comment.userName}
								body={comment.body}
								date={comment.commentDate}
								reactions={comment.reactions}
								impersonatedBy={comment.impersonatedBy}
							/>
							{comment.commentReplies.map((reply) => (
								<CommentItem
									key={reply.id}
									userId={reply.user}
									userName={reply.userName}
									body={reply.body}
									date={reply.commentDate}
									reactions={reply.reactions}
									isReply
									impersonatedBy={reply.impersonatedBy}
								/>
							))}
						</div>
					))}
				</div>

				{hasMore && (
					<button
						type="button"
						onClick={onLoadMore}
						disabled={isLoadingMore}
						className="w-full text-center py-2 text-[13px] text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
					>
						{isLoadingMore ? (
							<span className="flex items-center justify-center gap-2">
								<span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
								Loading...
							</span>
						) : (
							"Show more comments"
						)}
					</button>
				)}
			</div>
		</div>
	);
};

export default CommentThread;
